import React, { useState, useEffect, useCallback } from "react";
import { getDatabase, ref, get, onValue, off } from "firebase/database";
import { auth } from "../firebase/config";
import { FaDownload, FaUsers, FaShoppingCart, FaTruck, FaCheckCircle, FaClock, FaEye } from "react-icons/fa";
import AdminLayout from "../components/AdminLayout";
import "./AdminDashboard.css";
import { setPageMeta } from "../utils/seo";

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");
  const [deliveryStatuses, setDeliveryStatuses] = useState({});
  const [loadingDelivery, setLoadingDelivery] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("info");

  useEffect(() => {
    setPageMeta({
      title: "Admin Dashboard | Banat India",
      description: "",
      keywords: "",
      robots: "noindex, nofollow",
    });
  }, []);

  // Toast notification function
  const showToastMessage = useCallback((message, type = "info") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
      setToastMessage("");
    }, 15000); // 15 seconds instead of 4
  }, []);

  // Function to fetch delivery status from Shiprocket
  const fetchDeliveryStatus = async (shipmentId) => {
    if (!shipmentId) return null;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://shiprocket-backend-maeh.onrender.com';
      const response = await fetch(`${backendUrl}/track-shipment/${shipmentId}`);
      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        
        return null;
      }
    } catch (error) {
      
      return null;
    }
  };

  // Function to get delivery status for all orders
  const loadDeliveryStatuses = useCallback(async (ordersList) => {
    const statuses = {};
    const loadingStates = {};
    
    for (const order of ordersList) {
      if (order.shiprocket_order_id || order.shipment_id) {
        const shipmentId = order.shiprocket_order_id || order.shipment_id;
        loadingStates[order.orderId] = true;
        
        try {
          const deliveryData = await fetchDeliveryStatus(shipmentId);
          if (deliveryData) {
            statuses[order.orderId] = {
              status: deliveryData.status || 'Unknown',
              awb: deliveryData.awb || 'N/A',
              courier: deliveryData.courier_name || 'N/A',
              etd: deliveryData.etd || null,
              trackUrl: deliveryData.track_url || null
            };
          } else {
            statuses[order.orderId] = {
              status: 'Not Available',
              awb: 'N/A',
              courier: 'N/A',
              etd: null,
              trackUrl: null
            };
          }
        } catch (error) {
          
          statuses[order.orderId] = {
            status: 'Tracking Unavailable',
            awb: 'N/A',
            courier: 'N/A',
            etd: null,
            trackUrl: null
          };
        }
        
        loadingStates[order.orderId] = false;
      }
    }
    
    setDeliveryStatuses(statuses);
    setLoadingDelivery(loadingStates);
  }, []);

  // Function to get delivery status icon
  const getDeliveryStatusIcon = (status) => {
    if (!status) return <FaClock />;
    
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('delivered') || lowerStatus.includes('completed')) {
      return <FaCheckCircle style={{ color: '#28a745' }} />;
    } else if (lowerStatus.includes('in transit') || lowerStatus.includes('shipped')) {
      return <FaTruck style={{ color: '#007bff' }} />;
    } else if (lowerStatus.includes('pending') || lowerStatus.includes('processing')) {
      return <FaClock style={{ color: '#ffc107' }} />;
    } else {
      return <FaClock style={{ color: '#6c757d' }} />;
    }
  };



  // Function to get delivery status color
  const getDeliveryStatusColor = (status) => {
    if (!status) return '#6c757d';
    
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('delivered') || lowerStatus.includes('completed')) {
      return '#28a745';
    } else if (lowerStatus.includes('in transit') || lowerStatus.includes('shipped')) {
      return '#007bff';
    } else if (lowerStatus.includes('pending') || lowerStatus.includes('processing')) {
      return '#ffc107';
    } else {
      return '#6c757d';
    }
  };

  useEffect(() => {
    const database = getDatabase();
    const usersRef = ref(database, "users");
    const ordersRef = ref(database, "orders");
    
    
    
    
    
    
    
    
    
    
    // Check authentication status
    const currentUser = auth.currentUser;
    
    
    if (currentUser) {
      
      
    }
    
    let usersData = {};
    let ordersData = {};
    
    // Function to process and combine data
    const processData = () => {
      try {
        
        
        
        
        const usersList = [];
        const allOrders = [];
        
        // Process all users from users path
        Object.entries(usersData).forEach(([userId, userProfile]) => {
          if (userProfile && typeof userProfile === 'object') {
            // Get orders for this user from orders path
            const userOrders = ordersData[userId] || {};
            let userOrderList = [];
            
            if (userOrders && typeof userOrders === 'object') {
              if (Array.isArray(userOrders)) {
                userOrderList = userOrders.map((order, index) => ({
                  orderId: `order-${index}`,
                  userId: userId,
                  ...order
                }));
              } else {
                userOrderList = Object.entries(userOrders).map(([orderKey, order]) => ({
                  orderId: orderKey,
                  userId: userId,
                  ...order
                }));
              }
            }
            
            // Add to all orders
            allOrders.push(...userOrderList);
            
            // Create comprehensive user summary
            const userSummary = {
              userId: userId,
              email: userProfile.email || 'N/A',
              displayName: userProfile.displayName || 'N/A',
              phoneNumber: userProfile.phoneNumber || 'N/A',
              createdAt: userProfile.createdAt || 'N/A',
              lastLogin: userProfile.lastLogin || 'N/A',
              isActive: userProfile.isActive !== false,
              totalOrders: userOrderList.length,
              totalSpent: userOrderList.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0),
              lastOrder: userOrderList.length > 0 ? userOrderList[userOrderList.length - 1] : null,
              activeOrders: userOrderList.filter(order => order.status === 'paid' || order.status === 'pending').length,
              hasOrders: userOrderList.length > 0
            };
            
            usersList.push(userSummary);
          }
        });
        
        // Sort users by creation date (newest first)
        usersList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
        
        
        
        
        
        
        setUsers(usersList);
        setOrders(allOrders);
        
        // Load delivery statuses for all orders
        if (allOrders.length > 0) {
          loadDeliveryStatuses(allOrders);
        }
        
        setLoading(false);
      } catch (error) {
        
        
        setUsers([]);
        setOrders([]);
        setLoading(false);
      }
    };
    
    // Set up real-time listener for users
    const unsubscribeUsers = onValue(usersRef, (snapshot) => {
      
      if (snapshot.exists()) {
        usersData = snapshot.val();
        
      } else {
        usersData = {};
        
      }
      processData();
    }, (error) => {
      
      setLoading(false);
    });
    
    // Set up real-time listener for orders
    const unsubscribeOrders = onValue(ordersRef, (snapshot) => {
      
      if (snapshot.exists()) {
        ordersData = snapshot.val();
        
      } else {
        ordersData = {};
        
      }
      processData();
    }, (error) => {
      
      setLoading(false);
    });
    
    // Cleanup function to unsubscribe from real-time listeners
    return () => {
      
      off(usersRef, 'value', unsubscribeUsers);
      off(ordersRef, 'value', unsubscribeOrders);
    };
  }, [loadDeliveryStatuses]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Manual refresh function
  const refreshData = useCallback(async () => {
    try {
      setLoading(true);
      
      
      // Force a fresh fetch from both users and orders database
      const database = getDatabase();
      const usersRef = ref(database, "users");
      const ordersRef = ref(database, "orders");
      
      const [usersSnapshot, ordersSnapshot] = await Promise.all([
        get(usersRef),
        get(ordersRef)
      ]);
      
      const usersData = usersSnapshot.exists() ? usersSnapshot.val() : {};
      const ordersData = ordersSnapshot.exists() ? ordersSnapshot.val() : {};
      
      
      
      
      const usersList = [];
      const allOrders = [];

      // Process all users from users path
      Object.entries(usersData).forEach(([userId, userProfile]) => {
        if (userProfile && typeof userProfile === 'object') {
          // Get orders for this user from orders path
          const userOrders = ordersData[userId] || {};
          let userOrderList = [];
          
          if (userOrders && typeof userOrders === 'object') {
            if (Array.isArray(userOrders)) {
              userOrderList = userOrders.map((order, index) => ({
                orderId: `order-${index}`,
                userId: userId,
                ...order
              }));
            } else {
              userOrderList = Object.entries(userOrders).map(([orderKey, order]) => ({
                orderId: orderKey,
                userId: userId,
                ...order
              }));
            }
          }
          
          // Add to all orders
          allOrders.push(...userOrderList);
          
          // Create comprehensive user summary
          const userSummary = {
            userId: userId,
            email: userProfile.email || 'N/A',
            displayName: userProfile.displayName || 'N/A',
            phoneNumber: userProfile.phoneNumber || 'N/A',
            createdAt: userProfile.createdAt || 'N/A',
            lastLogin: userProfile.lastLogin || 'N/A',
            isActive: userProfile.isActive !== false,
            totalOrders: userOrderList.length,
            totalSpent: userOrderList.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0),
            lastOrder: userOrderList.length > 0 ? userOrderList[userOrderList.length - 1] : null,
            activeOrders: userOrderList.filter(order => order.status === 'paid' || order.status === 'pending').length,
            hasOrders: userOrderList.length > 0
          };
          
          usersList.push(userSummary);
        }
      });
      
      // Sort users by creation date (newest first)
      usersList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setUsers(usersList);
      setOrders(allOrders);
      
      if (allOrders.length > 0) {
        loadDeliveryStatuses(allOrders);
      }
      
      const usersWithOrders = usersList.filter(u => u.hasOrders).length;
      const usersWithoutOrders = usersList.filter(u => !u.hasOrders).length;
      
      showToastMessage(`✅ Data refreshed successfully!\n📊 Total Users: ${usersList.length}\n🛒 Users with Orders: ${usersWithOrders}\n👤 Users without Orders: ${usersWithoutOrders}\n📦 Total Orders: ${allOrders.length}`, "success");
    } catch (error) {
      
      showToastMessage(`❌ Error refreshing data: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [loadDeliveryStatuses, showToastMessage]);

  const exportData = () => {
    const data = {
      users: users,
      orders: orders,
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-data-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };





  if (loading) {
    return <div className="admin-loading">Loading admin data...</div>;
  }



  return (
    <AdminLayout>
      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: toastType === 'info' ? '#17a2b8' : '#28a745',
          color: 'white',
          padding: '20px 25px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          fontSize: '12px',
          fontWeight: 'normal',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflowY: 'auto',
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          animation: 'slideIn 0.3s ease-out',
          border: '2px solid rgba(255,255,255,0.2)'
        }}>
          {toastMessage}
        </div>
      )}
      
      <div className="admin-content-header">
        <h1>Admin Dashboard</h1>
        <div className="admin-actions">
          <button className="export-btn" onClick={exportData}>
            <FaDownload /> Export Data
          </button>
          <button 
            className="export-btn" 
            onClick={refreshData}
            style={{ backgroundColor: '#17a2b8' }}
          >
            🔄 Refresh Data
          </button>
          <button 
            className="export-btn" 
            onClick={() => loadDeliveryStatuses(orders)}
            style={{ backgroundColor: '#28a745' }}
          >
            🚚 Refresh Delivery Status
          </button>
          <button 
            className="export-btn" 
            onClick={() => {
              setDeliveryStatuses({});
              setLoadingDelivery({});
            }}
            style={{ backgroundColor: '#dc3545' }}
          >
            ❌ Disable Tracking
          </button>

        </div>
      </div>



      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === "users" ? "active" : ""}`}
          onClick={() => handleTabChange("users")}
        >
          <FaUsers /> Users
        </button>
        <button 
          className={`tab-btn ${activeTab === "orders" ? "active" : ""}`}
          onClick={() => handleTabChange("orders")}
        >
          <FaShoppingCart /> All Orders
        </button>
      </div>

      {activeTab === "users" && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Users</h3>
              <p className="stat-number">{users.length}</p>
              <div className="stat-detail">
                <small>All registered users</small>
              </div>
            </div>
            <div className="stat-card">
              <h3>Users with Orders</h3>
              <p className="stat-number">{users.filter(u => u.hasOrders).length}</p>
              <div className="stat-detail">
                <small>{((users.filter(u => u.hasOrders).length / users.length) * 100).toFixed(1)}% of total</small>
              </div>
            </div>
            <div className="stat-card">
              <h3>Users without Orders</h3>
              <p className="stat-number">{users.filter(u => !u.hasOrders).length}</p>
              <div className="stat-detail">
                <small>Registered but no purchases</small>
              </div>
            </div>
            <div className="stat-card">
              <h3>Total Orders</h3>
              <p className="stat-number">{orders.length}</p>
              <div className="stat-detail">
                <small>All time orders</small>
              </div>
            </div>
            <div className="stat-card">
              <h3>Total Revenue</h3>
              <p className="stat-number">₹{orders.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0).toFixed(2)}</p>
              <div className="stat-detail">
                <small>All time revenue</small>
              </div>
            </div>
            <div className="stat-card">
              <h3>Active Orders</h3>
              <p className="stat-number">{orders.filter(order => order.status === 'paid' || order.status === 'pending').length}</p>
              <div className="stat-detail">
                <small>Pending/Paid orders</small>
              </div>
            </div>
          </div>

          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Total Orders</th>
                  <th>Total Spent</th>
                  <th>Active Orders</th>
                  <th>Last Order</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.userId}>
                    <td className="user-id-cell">
                      <div className="user-id-short">
                        {user.userId.substring(0, 8)}...
                      </div>
                      <div className="user-id-full" style={{ fontSize: '10px', color: '#666' }}>
                        {user.userId}
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>{user.displayName}</td>
                    <td>{user.phoneNumber}</td>
                    <td>
                      <span className={`order-count ${user.totalOrders > 0 ? 'has-orders' : 'no-orders'}`}>
                        {user.totalOrders}
                      </span>
                    </td>
                    <td>
                      <span className={`spent-amount ${user.totalSpent > 0 ? 'has-spent' : 'no-spent'}`}>
                        ₹{user.totalSpent.toFixed(2)}
                      </span>
                    </td>
                    <td>
                      <span className={`active-orders ${user.activeOrders > 0 ? 'has-active' : 'no-active'}`}>
                        {user.activeOrders}
                      </span>
                    </td>
                    <td>
                      {user.lastOrder ? (
                        <div className="last-order-info">
                          <div>₹{user.lastOrder.total}</div>
                          <div className="order-date">
                            {new Date(user.lastOrder.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      ) : (
                        <span className="no-orders-text">No orders</span>
                      )}
                    </td>
                    <td>
                      <div className="join-date">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td>
                      <span className={`user-status ${user.isActive ? 'active' : 'inactive'}`}>
                        {user.isActive ? '✅ Active' : '❌ Inactive'}
                      </span>
                    </td>
                    <td>
                      <button 
                        className="view-details-btn"
                        onClick={() => showToastMessage(`👤 Complete User Details:\n\nUser ID: ${user.userId}\nEmail: ${user.email}\nName: ${user.displayName}\nPhone: ${user.phoneNumber}\nJoined: ${user.createdAt}\nLast Login: ${user.lastLogin}\nStatus: ${user.isActive ? 'Active' : 'Inactive'}\nTotal Orders: ${user.totalOrders}\nTotal Spent: ₹${user.totalSpent.toFixed(2)}\nActive Orders: ${user.activeOrders}\nHas Orders: ${user.hasOrders ? 'Yes' : 'No'}`, "info")}
                      >
                        <FaEye /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {users.length === 0 && (
            <div className="no-users">
              <FaUsers />
              <p>No users found.</p>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                This could mean no orders have been placed yet, or orders are stored in a different database path.
              </p>
            </div>
          )}
        </>
      )}

      {activeTab === "orders" && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Orders</h3>
              <p className="stat-number">{orders.length}</p>
            </div>
            <div className="stat-card">
              <h3>Paid Orders</h3>
              <p className="stat-number">{orders.filter(order => order.status === 'paid').length}</p>
            </div>
            <div className="stat-card">
              <h3>Pending Orders</h3>
              <p className="stat-number">{orders.filter(order => order.status === 'pending').length}</p>
            </div>
            <div className="stat-card">
              <h3>Total Revenue</h3>
              <p className="stat-number">₹{orders.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0).toFixed(2)}</p>
            </div>
            <div className="stat-card">
              <h3>Delivered Orders</h3>
              <p className="stat-number">
                {Object.values(deliveryStatuses).filter(status => 
                  status && status.status && status.status.toLowerCase().includes('delivered')
                ).length}
              </p>
            </div>
            <div className="stat-card">
              <h3>In Transit</h3>
              <p className="stat-number">
                {Object.values(deliveryStatuses).filter(status => 
                  status && status.status && (
                    status.status.toLowerCase().includes('transit') || 
                    status.status.toLowerCase().includes('shipped')
                  )
                ).length}
              </p>
            </div>
          </div>

          <div className="orders-table">
            <table>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>User ID</th>
                  <th>Status</th>
                  <th>Total</th>
                  <th>Payment ID</th>
                  <th>Shiprocket Order</th>
                  <th>AWB Number</th>
                  <th>Delivery Status</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const deliveryStatus = deliveryStatuses[order.orderId];
                  const isLoadingDelivery = loadingDelivery[order.orderId];
                  
                  return (
                    <tr key={order.orderId}>
                      <td>{order.orderId}</td>
                      <td>{order.userId}</td>
                      <td>
                        <span className={`status-badge ${order.status || 'unknown'}`}>
                          {order.status || 'unknown'}
                        </span>
                      </td>
                      <td>₹{order.total || '0'}</td>
                      <td>{order.paymentId || 'N/A'}</td>
                      <td>{order.shiprocket_order_id || 'N/A'}</td>
                      <td>{order.shiprocket_awb || 'N/A'}</td>
                      <td>
                        {isLoadingDelivery ? (
                          <div className="delivery-loading">🔄 Loading...</div>
                        ) : deliveryStatus ? (
                          <div className="delivery-status">
                            <div className="delivery-status-main">
                              {getDeliveryStatusIcon(deliveryStatus.status)}
                              <span 
                                className="delivery-status-text"
                                style={{ color: getDeliveryStatusColor(deliveryStatus.status) }}
                              >
                                {deliveryStatus.status}
                              </span>
                            </div>
                            {deliveryStatus.courier !== 'N/A' && (
                              <div className="delivery-courier">
                                <small>🚛 {deliveryStatus.courier}</small>
                              </div>
                            )}
                            {deliveryStatus.etd && (
                              <div className="delivery-etd">
                                <small>📅 ETA: {new Date(deliveryStatus.etd).toLocaleDateString()}</small>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="delivery-no-data">
                            <span>❌ No tracking data</span>
                          </div>
                        )}
                      </td>
                      <td>{order.createdAt ? new Date(order.createdAt).toLocaleString() : 'N/A'}</td>
                      <td>
                        <button 
                          className="view-details-btn"
                          onClick={() => alert(`Order Details:\n${JSON.stringify(order, null, 2)}`)}
                        >
                          <FaEye /> View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {orders.length === 0 && (
            <div className="no-orders">
              <FaShoppingCart />
              <p>No orders found.</p>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                This could mean no orders have been placed yet, or orders are stored in a different database path.
              </p>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard; 